import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { entries, keyBy, keys, mergeWith, values } from 'lodash';
import { Text, Box, Checkbox, Flex, Input, useToast } from '@chakra-ui/react';
import { axiosInstance } from '../../../api/init';
import useForm, { TField } from '../../form/Form';
import { TInput } from '../../tabs/type';
import { isEmpty, strNomalize } from '../../../helpers';
import {
  addressInputs,
  addressInputskeysImport,
  typesOfInputOptions,
} from './ManageInputs';
import { addUploadMatch, fetchMatch } from '../../../api/tabsApi';
import RSelect from '../../form/RSelect';
import { TState, useActions } from '../../../redux/store';
import Modal from '../../modal/Modal';

const ManageImport = () => {
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const modalData = useSelector((state: TState) => state.modalData);
  const { onClose } = useActions();
  const toast = useToast();

  const [isNew, setIsNew] = useState<boolean[]>([]);
  const [currMatch, setCurrMatch] = useState({});
  const [duplicate, setDuplicate] = useState<string[]>([]);

  const { data, file } = modalData || [];
  const [headers] = data || [];

  const allInputs: TInput[] = dataInputs?.reduce(
    (prev, input) =>
      input.name_id === 'planning_type'
        ? prev
        : input.type !== 'address'
        ? [...prev, input]
        : [
            ...prev,
            ...addressInputskeysImport.map((key, i) => ({
              ...input,
              label: `Adresse ${input.label} ${addressInputs[i].toUpperCase()}`,
              name_id: `${input.name_id}_${key}`,
            })),
          ],
    [] as any,
  );

  const inputsByNameId = keyBy(allInputs, 'name_id');

  const nonExistingHeaders = headers?.filter(
    (header: string) =>
      !inputsByNameId[
        strNomalize(header.replace(/adresse|ville|code|postal/gi, '').trim())
      ],
  );

  const fields: TField[] = headers?.map((header: any) => ({
    label: '',
    name: strNomalize(header),
    type: 'text',
  }));

  const matchFields: TField[] = headers?.map((header: any, idx: number) => ({
    label: '',
    name: strNomalize(header),
    type: 'select',
    options: isNew[idx]
      ? typesOfInputOptions
      : allInputs?.map((input) => ({
          label: `${input.label}${
            strNomalize(input.name_id) !== strNomalize(input.label)
              ? ` (${input.name_id})`
              : ''
          } / ${input.type}${
            input.description ? ` / ${input.description}` : ''
          }`,
          value: input.name_id,
        })),
    placeholder: isNew[idx] ? 'Choisir un type' : 'Choisir un champ',
  }));

  const { Form } = useForm(fields);
  const {
    Form: MatchForm,
    formData: matchFormData,
    setFormData,
    setErrors,
  } = useForm(matchFields, {
    noRowData: true,
    onChange: (label: string) => {
      setErrors((errors) => {
        const newErrors = { ...errors };
        delete newErrors[label];
        return newErrors;
      });
    },
  });

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file first!');
      return;
    }

    const missings: string[] = [];

    const finalFormData: typeof matchFormData = entries(matchFormData).reduce(
      (prev, [k, v]) => (isEmpty(v) ? prev : { ...prev, [k]: v }),
      {},
    );

    const duplicates: string[] = [];
    const finalNames: string[] = [];

    const matchs = fields.reduce(
      (prev: Record<string, string>, { name: headerName }) => {
        const originalKey = headerName as string;
        const v = finalFormData[originalKey] || '';

        if (isEmpty(v)) return prev;

        const isNewKey = finalFormData[`${headerName}-isNew`];
        const newKey = finalFormData[`${headerName}-name`];

        const isAddress = v.match(/(_street|_city|_postal|_all)$/)?.[0];
        const matchKey = isAddress
          ? `adresse${newKey ? `_${newKey}` : ''}${isAddress}`
          : newKey || originalKey;

        if (isNewKey) {
          if (!finalFormData[originalKey]) {
            missings.push(originalKey);
          }

          if (
            (!isAddress &&
              ((newKey && inputsByNameId[newKey]) ||
                (!newKey && inputsByNameId[originalKey]))) ||
            finalNames.includes(matchKey)
          ) {
            duplicates.push(originalKey);
          }

          finalNames.push(matchKey);
        }

        return {
          ...prev,
          [originalKey]: isNewKey ? matchKey : v,
        };
      },
      {},
    );

    setErrors(missings.reduce((prev, k) => ({ ...prev, [k]: 'missings' }), {}));

    if (missings.length || duplicates.length) {
      if (missings.length) {
        toast({
          title: `Certains champ ne sont pas entièrement rempli (${missings.join(
            ', ',
          )})`,
          status: 'error',
        });
      }

      if (duplicates.length) {
        toast({
          title: `Noms déjà existant (${duplicates.join(', ')})`,
          status: 'error',
        });

        setDuplicate(duplicates);
      }

      return;
    }

    keys(matchs).length && (await addUploadMatch(matchs));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('headType', JSON.stringify(finalFormData));
    // formData.append('ignoreDuplicate', JSON.stringify(ignoreDuplicate));

    try {
      await axiosInstance.post('/api/data/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onClose();
    } catch (error) {
      console.error('Error uploading the file', error);
    }
  };

  useEffect(() => {
    setIsNew(() => Array(nonExistingHeaders?.length).fill(!allInputs.length));
  }, [nonExistingHeaders?.length, file]);

  useEffect(() => {
    const getMatchs = async () => {
      const respMatch = await fetchMatch();
      const fieldsByName = keyBy(fields, 'name');

      const filteredMatch = entries(respMatch?.data).reduce(
        (prev, [k, v]: any) =>
          inputsByNameId[v] && fieldsByName[k] ? { ...prev, [k]: v } : prev,
        {},
      );

      setCurrMatch(filteredMatch);
    };

    setDuplicate([]);
    setErrors({});

    file && getMatchs();
  }, [file]);

  useEffect(() => {
    const allMatch = headers?.reduce((prev: any, header: string) => {
      const id = inputsByNameId[strNomalize(header)]?.name_id;

      return id ? { ...prev, [id]: id } : prev;
    }, {});

    setFormData({ ...allMatch, ...currMatch });
  }, [currMatch]);

  const options = values(matchFormData).reduce((prev, v) => {
    const formatedValue = `${v}`.replace(/_street|_city|_postal|_all/g, '');
    const addressInput = inputsByNameId[v];

    return (/adresse|address/.test(formatedValue) ||
      addressInput?.type === 'address') &&
      !prev.some((p: any) => p.value === formatedValue)
      ? [
          ...prev,
          {
            label: formatedValue,
            value: formatedValue,
          },
        ]
      : prev;
  }, []);

  const renderManageImport = (
    <>
      <Text fontStyle='italic' mb={2} textAlign='center' w='50%' ml='50%'>
        Toute ligne vide sera ignoré
      </Text>
      {Form.map((F, idx) => (
        <Flex alignItems='center'>
          <Box w='20%' mr={3}>
            {F}
          </Box>
          <Flex>
            <Text>Nouveau ?</Text>
            <Checkbox
              mx={3}
              isChecked={isNew[idx]}
              onChange={(e) => {
                setFormData((fd) => ({
                  ...fd,
                  [`${strNomalize(headers[idx])}`]: undefined,
                  [`${strNomalize(headers[idx])}-isNew`]: e.target.checked,
                  [`${strNomalize(headers[idx])}-name`]: undefined,
                  [`${strNomalize(headers[idx])}-description`]: undefined,
                }));

                setIsNew((n) => {
                  const nn = [...n];
                  nn[idx] = e.target.checked;
                  return nn;
                });

                setErrors((errors) => {
                  const newErrors = { ...errors };
                  delete newErrors[`${strNomalize(headers[idx])}`];
                  return newErrors;
                });
              }}
            />
          </Flex>
          <Flex flexGrow={1} alignItems='center' gap={3}>
            {MatchForm[idx]}
            {isNew[idx] &&
              !/files/.test(matchFormData[strNomalize(headers[idx])]) && (
                <>
                  <Input
                    border={
                      duplicate.includes(strNomalize(headers[idx]))
                        ? '1px solid red'
                        : '1px solid #e2e8f0'
                    }
                    placeholder="Nom du champ (si vide, nom du champ de l'import)"
                    onChange={(e) => {
                      setFormData((fd) => ({
                        ...fd,
                        [`${strNomalize(headers[idx])}-name`]: e.target.value,
                      }));

                      setErrors((errors) => {
                        const newErrors = { ...errors };
                        delete newErrors[`${strNomalize(headers[idx])}`];
                        return newErrors;
                      });

                      setDuplicate((dups) =>
                        dups.filter(
                          (d) => d !== `${strNomalize(headers[idx])}`,
                        ),
                      );
                    }}
                  />
                  {!/planning/.test(
                    matchFormData[strNomalize(headers[idx])],
                  ) && (
                    <Input
                      placeholder='Description (facultatif)'
                      onChange={(e) =>
                        setFormData((fd) => ({
                          ...fd,
                          [`${strNomalize(headers[idx])}-description`]:
                            e.target.value,
                        }))
                      }
                    />
                  )}
                </>
              )}
            {isNew[idx] &&
              /number|planning/.test(
                matchFormData[strNomalize(headers[idx])],
              ) && (
                <>
                  {/^number/.test(matchFormData[strNomalize(headers[idx])]) && (
                    <Input
                      placeholder='Unité'
                      onChange={(e) =>
                        setFormData((fd) => ({
                          ...fd,
                          [`${strNomalize(headers[idx])}-unity`]:
                            e.target.value,
                        }))
                      }
                    />
                  )}
                  {/planning/.test(
                    matchFormData[strNomalize(headers[idx])],
                  ) && (
                    <>
                      <RSelect
                        placeholder='Utilisateurs associés aux RDV'
                        options={entries(matchFormData).reduce(
                          (prev, [k, v]) =>
                            v === 'users' || inputsByNameId[v]?.type === 'users'
                              ? [
                                  ...prev,
                                  {
                                    label: k,
                                    value: k,
                                  },
                                ]
                              : prev,
                          [] as any,
                        )}
                        onChange={(e) => {
                          setFormData((fd) =>
                            mergeWith(fd, {
                              [`${strNomalize(headers[idx])}_planningUser`]:
                                e?.value || null,
                            }),
                          );
                        }}
                      />
                      <RSelect
                        placeholder='Adresse du RDV'
                        options={options}
                        onChange={(e) =>
                          setFormData((fd) =>
                            mergeWith(fd, {
                              [`${strNomalize(headers[idx])}_planningAddress`]:
                                e?.value || null,
                            }),
                          )
                        }
                      />
                      <RSelect
                        placeholder='Champ confirmation'
                        options={headers.map((h: string) => ({
                          label: h,
                          value: strNomalize(h),
                        }))}
                        onChange={(e) =>
                          setFormData((fd) =>
                            mergeWith(fd, {
                              [`${strNomalize(headers[idx])}_planningConfirm`]:
                                e?.value || null,
                            }),
                          )
                        }
                      />
                      <RSelect
                        placeholder='Champ telepro'
                        options={entries(matchFormData).reduce(
                          (prev, [k, v]) =>
                            v === 'users' || inputsByNameId[v]?.type === 'users'
                              ? [
                                  ...prev,
                                  {
                                    label: k,
                                    value: k,
                                  },
                                ]
                              : prev,
                          [] as any,
                        )}
                        onChange={(e) =>
                          setFormData((fd) =>
                            mergeWith(fd, {
                              [`${strNomalize(headers[idx])}_planningTelepro`]:
                                e?.value || null,
                            }),
                          )
                        }
                      />
                    </>
                  )}
                </>
              )}
          </Flex>
        </Flex>
      ))}
    </>
  );

  const modalOptions = {
    computeBeforeImport: {
      component: renderManageImport,
      action: handleUpload,
      title: 'Import correspondance',
      actionLabel: 'Importer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManageImport;
